/**
 * Reading an input as a possible answer to a parked completion question —
 * whether it is shaped like one, the splice, and the two in-context refusals
 * the run may need (D37/D38, §3.2/D84). Its own module since D179 for no
 * deeper reason than the conversation wrapper reaching the file-size cap; it
 * belongs beside the completion model it reads. The shape test joined it at
 * D247, when the completion model reached the cap in turn.
 *
 * @packageDocumentation
 */

import type { RuntimeState } from '@orpheus/content-schema';

import { type EntityRef, foldCharacters, type ParseCandidateEntity, parseCommand } from '@orpheus/parser-engine';

import type { ActionRejection } from '../actions/runtime-action.js';
import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';
import type { SpokenResponse } from '../defaults/default-responses.js';

import { deriveEntityCatalog } from '../entities/derive-entity-catalog.js';
import { bareNounObjectRef } from './bare-noun.js';
import { clauseCatalogOf, type RuledOutRefs } from './clause-settlement.js';
import { buildParseContext } from './parse-context.js';
import { type PendingCompletion, refusalForFailedAnswer, refusalForRefusedAnswer, spliceAnsweredInput } from './pending-completion.js';

/**
 * Read one input as a possible ANSWER to the parked completion question: the
 * splice, and the two in-context refusals the run may need — the D38 override
 * for an answer that resolves to nothing, and the §3.2/D84 override for one
 * that resolves and is then refused (spoken in the question's terms).
 *
 * D245 (the Meridian play-test's A1): an input that is NOT an answer and is
 * a command of the player's own moves on from the question, which lets go —
 * see {@link isCommandOfTheirOwn}.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the input
 * @param completion - The parked completion question, if any
 * @param rawInput - Exactly what the player typed
 * @returns The parse text (the splice, when this input answers), the answer-context overrides, the clause as settled (D247), and whether the player moved on
 */
export const readAnswerAttempt = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	completion: Parameters<typeof spliceAnsweredInput>[0] | undefined,
	rawInput: string,
): {
	readonly answerRefusal: SpokenResponse | undefined;
	readonly answerRejectionOverride: ((rejection: ActionRejection) => SpokenResponse | undefined) | undefined;
	readonly clauseCatalog: undefined | readonly ParseCandidateEntity[];
	readonly isAnswer: boolean;
	readonly movesOn: boolean;
	readonly parseText: string;
	readonly ruledOutRefs: RuledOutRefs;
} => {
	if (completion === undefined) {
		return {
			answerRefusal: undefined,
			answerRejectionOverride: undefined,
			clauseCatalog: undefined,
			isAnswer: false,
			movesOn: false,
			parseText: rawInput,
			ruledOutRefs: undefined,
		};
	}

	if (!isAnswerShaped(bundle, state, rawInput, completion)) {
		return {
			answerRefusal: undefined,
			answerRejectionOverride: undefined,
			clauseCatalog: undefined,
			isAnswer: false,
			movesOn: isCommandOfTheirOwn(bundle, state, rawInput),
			parseText: rawInput,
			ruledOutRefs: undefined,
		};
	}

	return {
		answerRefusal: refusalForFailedAnswer(bundle, state, completion, rawInput),
		answerRejectionOverride: refusalForRefusedAnswer(bundle, state, completion),
		// D247: the splice is the asking clause still, read as the player
		// settled it — with the thing the answer names by its own words, even
		// one the clause ruled out for its own word.
		clauseCatalog: clauseCatalogOf(bundle, state, completion.ruledOutRefs, bareNounObjectRef(bundle, state, rawInput)),
		isAnswer: true,
		movesOn: false,
		parseText: spliceAnsweredInput(completion, rawInput),
		ruledOutRefs: completion.ruledOutRefs,
	};
};

/**
 * Is this input shaped like an ANSWER to the parked question rather than a new
 * command? The question decides what an answer looks like: "Open it with
 * what?" wants a bare noun (no directive of its own), and "What would you like
 * to do with the kettle?" wants a bare verb (a directive still missing its
 * object). Anything else — a complete command — is the player moving on, and
 * ends the thread.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the input
 * @param rawInput - Exactly what the player typed
 * @param pendingCompletion - The parked question
 * @returns Whether the input answers it
 */
const isAnswerShaped = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	rawInput: string,
	pendingCompletion: PendingCompletion,
): boolean => {
	if (rawInput.trim() === '') {
		return false;
	}

	/*
	 * F4 (§8.3, D80): a question is never an answer. An input led by one of the
	 * vocabulary's interrogative leaders ("what is happening?") is the player
	 * asking their OWN question — splicing it into the parked one quoted their
	 * whole sentence back as a failed name, question mark and all. It runs as
	 * ordinary input instead (the D48 question-form machinery reads it), and
	 * the survival rules keep the parked question standing. The split-limit +
	 * join keeps the leading-word read total: a non-blank input always yields
	 * its first word, and no leader is the empty string.
	 */
	if (bundle.compiledLexicon.interrogativeLeaderSet.has(foldCharacters(rawInput).trim().split(/\s+/u, 1).join(''))) {
		return false;
	}

	/*
	 * D64 (Calder's sweep, §2.1 — the third act of the `pry` collision): in an
	 * answer slot, the input resolves as a NOUN PHRASE first. The question
	 * already supplied the verb, so there is no room for a second one — and
	 * without this, answering "Open it with what?" with the item's own full
	 * name failed: "pry bar" parsed as the verb `pry` aimed at the bar, and
	 * the player was told their correct answer did not answer. Typing LESS of
	 * the right name worked, which is the tell that the resolution order was
	 * upside down.
	 *
	 * The check is the PARSER's own bare-noun reading (D133), so "leads with
	 * the name of a thing" has exactly one definition — and it holds for a
	 * name that heads a chain ("crowbar and go north", D91), where the name
	 * answers and the rest is the player moving on. Verb-slot questions
	 * ("what would you like to do with it?") skip it — they are asking FOR a
	 * verb.
	 */
	if (pendingCompletion.prefixesAnswer !== true && bareNounObjectRef(bundle, state, rawInput) !== undefined) {
		return true;
	}

	const outcome = parseCommand(rawInput, {
		entities: deriveEntityCatalog(bundle, state),
		lexicon: bundle.compiledLexicon,
	});

	/*
	 * D247: a name that TIES is still a name. "Use it on what?" answered
	 * `cabinet`, with two cabinets in the room, ran as a bare noun and asked
	 * which cabinet about nothing — the question, and the key the player had
	 * already chosen, were gone. Spliced, the clause itself asks which, about
	 * the word the answer brought, and carries what it had settled. A tie led
	 * by a verb is a command of the player's own, as R33 reads one below.
	 */
	if (outcome.type === 'needs_clarification') {
		return pendingCompletion.prefixesAnswer !== true && outcome.parsedPrefix.length === 0 && !leadsWithAVerb(bundle, rawInput);
	}

	if (outcome.type !== 'unrecognized') {
		return false;
	}

	/*
	 * R21: a failing input whose PREFIX parsed is a CHAIN the player is
	 * running, not the name of a thing. "south and take the pry bar and
	 * north" under an open question read as `entity_not_found` here — the
	 * whole-input parse above draws its catalog from where the player is
	 * STANDING, so a mid-chain reference to the next room over cannot
	 * resolve — and the chain was spliced into the question as its "answer".
	 * A parsed prefix means the player issued a command and moved on; the
	 * walk, which re-parses each clause against the state its predecessors
	 * left, is what knows how the chain really ends.
	 */
	if (outcome.parsedPrefix.length > 0) {
		return false;
	}

	if (pendingCompletion.prefixesAnswer === true) {
		// A bare verb: the grammar recognized the intent and found no object,
		// which is exactly what "what would you like to do with it?" invites.
		return (
			outcome.diagnostics.failureKind === 'structure_not_understood' &&
			outcome.diagnostics.understoodIntentToken !== undefined &&
			outcome.diagnostics.unmatchedTokens.length === 0
		);
	}

	/*
	 * R33 (D113; Calder's §3 — the fifth door in the family): an input whose
	 * LEADING TOKEN is a known verb is a command attempt, whatever goes wrong
	 * downstream. The boundary is about the first token, not about where the
	 * failure happened — "search behind pipe" fails at object resolution, and
	 * the miss arm must not own it. This runs AFTER the whole-input noun
	 * resolution near the top, so D64's "pry bar" — a NAME that happens to
	 * lead with a verb — is already claimed as the answer it is; and after
	 * the verb-slot branch, whose whole business is inviting bare verbs.
	 */
	if (leadsWithAVerb(bundle, rawInput)) {
		return false;
	}

	// D45: a bare NOUN is what "open it with what?" invites — and keyboard mash
	// is not a noun. D28 already put the evidence on the diagnostics, so this
	// reuses that judgement rather than inventing a second one: an input with no
	// token that reads as language cannot be the name of a thing. Without this,
	// mash typed under an open question was spliced into it and answered with
	// "Nothing here answers to 'zxcvv sdfk'" — the gibberish register D28 built
	// was unreachable for as long as any question stayed parked.
	if (outcome.diagnostics.failureKind === 'no_intent_recognized') {
		return outcome.diagnostics.resemblesLanguage !== false;
	}

	// R28 and D225: an unknown word before a present thing that cannot be
	// describing it is the player's own command, and never an answer.
	if (isCommandAttemptOnAPresentThing(bundle, state, outcome.diagnostics)) {
		return false;
	}

	// D64's second finding, from Calder's every-name gate: "security door",
	// answered while the door is elsewhere, read as D36's unknown VERB
	// "security" aimed at the locker (which answers to "door") — the player
	// named an absent thing and was told a present thing refuses a verb they
	// never typed. In an answer slot the question invited a NOUN, so a reading
	// of unknown-word-plus-noun is an adjective-plus-noun that did not resolve:
	// splice it, and let the failed-answer path keep the question honest.
	return outcome.diagnostics.failureKind === 'unsupported_action' || outcome.diagnostics.failureKind === 'entity_not_found';
};

/**
 * Does this input begin with a verb — a known intent token, or the whole of a
 * multiword verb alias (R33/D113)?
 *
 * @remarks
 * R34 (D114) closed the rule's own gap: a verb can BEGIN as a multiword
 * alias whose first word is no verb alone — "pick up the tin", "put down the
 * kettle" — so the leading tokens are also matched against the multiword
 * table, exact-prefix. Prefix, not first-word membership: the answer "light"
 * must not become a command because "light on fire" exists. The
 * `slice().join()` read keeps the first word total without a default no
 * input could reach (§14.1): an empty input yields no token, and no verb is
 * the empty string.
 *
 * @param bundle - The loaded bundle
 * @param rawInput - Exactly what the player typed
 * @returns Whether the input leads with a verb
 */
const leadsWithAVerb = (bundle: EngineGameBundle, rawInput: string): boolean => {
	const inputTokens = foldCharacters(rawInput)
		.split(/\s+/u)
		.filter((token) => token !== '');
	const leadingToken = inputTokens.slice(0, 1).join('');
	const leadingAliases = bundle.compiledLexicon.multiwordAliasesByFirstToken.get(leadingToken) ?? [];

	return (
		bundle.compiledLexicon.intentsByToken.has(leadingToken) ||
		leadingAliases.some(
			(alias) =>
				alias.target.kind === 'intent' &&
				alias.tokenSequence.length <= inputTokens.length &&
				alias.tokenSequence.every((aliasToken, tokenIndex) => aliasToken === inputTokens[tokenIndex]),
		)
	);
};

/**
 * Is this failed reading a COMMAND ATTEMPT — an unknown word before a thing
 * that is here, where the word cannot be describing the thing?
 *
 * @remarks
 * The parser's D36 structural reading already calls the pair a verb attempt
 * (`unsupported_action`, the leading word as `attemptedVerbSurface`); D64
 * overrode it in answer slots, where the question invited a NOUN and
 * "security door", typed while the door is elsewhere, is a name for an absent
 * thing. Two findings since drew the line at which the override stops:
 *
 * - **R28 (D104; Calder's D102 §1 verdict):** when the leading word is itself
 *   the name of a PRESENT thing ("feed plant" with the carton carried — `feed`
 *   is the carton's alias), two juxtaposed nouns are not a name in English;
 *   the ordinary path owns the better answer (the teach that names the verb
 *   and the thing). Without this, carrying an item TOOK a good answer away.
 * - **D225 (E3; Calder's play-test, every pack):** when the leading word is
 *   one the pack declares NOTHING by (`declaredWords`, D211 — things, never
 *   prose), it cannot be describing a thing of the pack's. "drink flask" and
 *   "pet dog" were spliced into "Open it with what?" and refused as "You
 *   cannot open the flask with drink flask — and the question still stands";
 *   they run as themselves now, the question waiting under the survival
 *   rules, as D218 ruled for a command under a which-one. "security door"
 *   keeps D64's reading: `security` is a word of the security door's. The
 *   parser folded the surface when it read it, as the index folds its words.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the input
 * @param diagnostics - The failed parse's diagnostics
 * @returns Whether the input is a command attempt the miss arm must not own
 */
const isCommandAttemptOnAPresentThing = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	diagnostics: { readonly attemptedVerbSurface?: string; readonly failureKind: string },
): boolean => {
	if (diagnostics.failureKind !== 'unsupported_action' || diagnostics.attemptedVerbSurface === undefined) {
		return false;
	}

	return (
		bareNounObjectRef(bundle, state, diagnostics.attemptedVerbSurface) !== undefined ||
		!bundle.indexes.declaredWords.has(diagnostics.attemptedVerbSurface)
	);
};

/**
 * D245: is this input a COMMAND of the player's own — a clause led by a verb
 * they typed (run or refused, alone or heading a chain), or an attempt with a
 * verb the world does not know (`lie on the couch`, D36's reading)?
 *
 * @remarks
 * A completion question ("Use it on what?") asks for one missing word. D79
 * let it wait through everything that was not an answer ("she waits"), and
 * D225 kept that for a command under it: `drink flask` ran as itself and the
 * question stood. The Meridian's play-test (finding 12) found what standing
 * costs: the player has moved on, and the next thing they type that reads as
 * a name is spliced into a sentence they have finished with. So a command of
 * their own lets the completion go, silently — the player's own sentence was
 * the reply, and there is nothing to announce. What is NOT a command keeps it
 * standing, as D79 ruled: noise, a name that resolves to nothing (D38's
 * in-context miss), a question of the player's own (F4: `what is the lamp`
 * reads as a question, not a verb), and a bare name (which answers, or
 * replaces the question with its own offer, D66).
 *
 * Read against the room as it stands before the input, with the recency the
 * input itself will be read with — `take it` is a command whenever `it`
 * means something.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the input
 * @param rawInput - Exactly what the player typed
 * @returns Whether the input is a command of the player's own
 */
const isCommandOfTheirOwn = (bundle: EngineGameBundle, state: RuntimeState, rawInput: string): boolean => {
	const outcome = parseCommand(
		rawInput,
		buildParseContext(bundle, deriveEntityCatalog(bundle, state), {
			recentEntityRefs: state.session.recentEntityRefs as unknown as readonly EntityRef[],
		}),
	);
	const ledCommands = outcome.type === 'parsed' ? outcome.commands : outcome.parsedPrefix;

	return (
		ledCommands.some((command) => command.rescuedAs !== 'bare_noun' && command.rescuedAs !== 'question') ||
		(outcome.type === 'unrecognized' && outcome.diagnostics.failureKind === 'unsupported_action')
	);
};

/**
 * D245: does this input move on from the completion question parked in the
 * state — a command of the player's own, not an answer — so that the question
 * lets go? The conversation's own decision, read whole, for a caller that
 * classifies what an input did (the language battery tells an answer from a
 * player moving on by it).
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the input
 * @param rawInput - Exactly what the player typed
 * @returns Whether a completion is parked and this input moves on from it
 */
export const movesOnFromQuestion = (bundle: EngineGameBundle, state: RuntimeState, rawInput: string): boolean => {
	const parked = state.session.pendingQuestion;

	return parked?.kind === 'completion' && readAnswerAttempt(bundle, state, parked, rawInput).movesOn;
};
